import React from 'react';
import { Box } from '@mui/material';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import LandingPage from './views/LandingPage';
import ForgotPasswordPage from './views/ForgotPasswordPage';
import AuthPage from './views/AuthPage';
import LoginPage from './views/LoginPage';
import './styles/wallet-button.css';
import { FlashOnStellarClientV2, StellarNetwork } from '@flashbacktech/flashbackclient/stellarv2';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/custom-toast.css';
import { ApiProvider, useApi } from './contexts/ApiContext';
import { useWithTokenRefresh } from './utils/api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MFAProvider, useMFA } from './contexts/MFAContext';
import { QuotaProvider } from './contexts/QuotaContext';
import { SuiWalletContextProvider } from './contexts/sui/SuiWalletContext';
import { UnifiedWeb3WalletProvider, WalletType } from './contexts/unified_wallet/UnifiedWeb3WalletContext';
// Components
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import waitingIntroVideo from './assets/video-intro/waiting-intro.mp4';

import FileExplorer from './views/FileExplorer';
import { TabIndex } from './navigation';
import QoSPerformanceDashboard from './views/QoSPerformanceDashboard';
import MetricsDashboard from './views/MetricsDashboard';
import AuthCallback from './components/AuthCallback';
import StorageUnits from './views/StorageUnits';
import StorageUnitDetails from './views/StorageUnitDetails';
import StorageRepos from './views/StorageRepos';
import StorageRepoDetails from './views/StorageRepoDetails';
import RepoOverview from './views/RepoOverview';
import BridgeNodes from './views/BridgeNodes';
import PrivateBridgeNodes from './views/PrivateBridgeNodes';
import Profile from './views/Profile';
import OverviewMockup from './views/OverviewMockup';
import CloudStorage from './views/CloudStorage';
import AddBucket from './views/AddBucket';
import AddRepo from './views/AddRepo';
import Feedback from './views/Feedback';
import Contact from './views/Contact';
import VerifyEmail from './views/VerifyEmail';
import MagicLinkActivatePage from './views/MagicLinkActivatePage';
import Activity from './views/Activity';
// Unified ISP Market pages
import DePINHubBuckets from './views/depin/DePINHubBuckets';
import DePINHubBucketDetails from './views/depin/DePINHubBucketDetails';
import DePINHubMarketplace from './views/depin/DePINHubMarketplace';
import DePINHubDeals from './views/depin/DePINHubDeals';
import AILLM from './views/AILLM';
import AILLMDetails from './views/AILLMDetails';
import AIStatistics from './views/AIStatistics';
import AIOverview from './views/AIOverview';
import AIPolicies from './views/AIPolicies';
import APIKeys from './views/APIKeys';
import ResourceAPIKeys from './views/ResourceAPIKeys';
import Home from './views/Home';
import ActivationLinks from './views/ActivationLinks';
import Organization from './views/Organization';
import PostLoginMFAVerification from './components/mfa/PostLoginMFAVerification';
import PostLoginMFAWrapper from './components/mfa/PostLoginMFAWrapper';
import ChatButton from './components/ChatButton';
import OnboardingTutorial, { TutorialReturnBubble } from './components/OnboardingTutorial';
import AccountSettingsTutorial from './components/AccountSettingsTutorial';
import Security2FAWarning from './components/Security2FAWarning';
// ProfileSetupWarning removed - unified into Security2FAWarning banner
import GatewayTutorial from './components/GatewayTutorial';
import WelcomeTutorial from './components/WelcomeTutorial';
import CloudGatewayTutorial from './components/CloudGatewayTutorial';
import CloudGatewayTutorialLauncher from './components/CloudGatewayTutorialLauncher';
import DePINHubTutorial from './components/depin/DePINHubTutorial';
import ProfileModal from './components/ProfileModal';
import Hub from './views/Hub';
import Chat from './views/Chat';
import DePINHub from './views/DePINHub';
import LandingPageV2 from './views/LandingPageV2';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box className="w-full">{children}</Box>}
    </div>
  );
}

// Ce composant n'utilise pas useAuth et peut être en dehors de AuthProvider
function AppLayout() {
  // Loading intro state — show video overlay after long idle (>4h)
  // Placed here (top-level) so it is independent of auth state and never interrupted by re-mounts
  const IDLE_THRESHOLD = 4 * 60 * 60 * 1000; // 4 hours in ms
  const [showIntroOverlay, setShowIntroOverlay] = React.useState(() => {
    if (!localStorage.getItem('authState')) return false; // Not logged in
    try {
      const lastActivity = localStorage.getItem('lastActivityTimestamp');
      if (!lastActivity) return true; // First visit ever → show
      const idleMs = Date.now() - parseInt(lastActivity, 10);
      return idleMs > IDLE_THRESHOLD;
    } catch { return false; }
  });
  const [introFadingOut, setIntroFadingOut] = React.useState(false);

  // Update activity timestamp on mount (so next load has a fresh stamp)
  React.useEffect(() => {
    try { localStorage.setItem('lastActivityTimestamp', String(Date.now())); } catch { /* ignore */ }
  }, []);

  // Keep activity timestamp fresh on tab visibility change
  React.useEffect(() => {
    const updateActivity = () => {
      if (!document.hidden) {
        try { localStorage.setItem('lastActivityTimestamp', String(Date.now())); } catch { /* ignore */ }
      }
    };
    document.addEventListener('visibilitychange', updateActivity);
    return () => document.removeEventListener('visibilitychange', updateActivity);
  }, []);

  // Fade-out handler: called when video ends naturally
  const handleIntroVideoEnded = React.useCallback(() => {
    setIntroFadingOut(true);
    setTimeout(() => setShowIntroOverlay(false), 600); // 600ms fade
  }, []);

  // Safety fallback: if video fails to load or play, dismiss after 6s
  React.useEffect(() => {
    if (!showIntroOverlay) return;
    const fallback = setTimeout(() => {
      if (!introFadingOut) {
        setIntroFadingOut(true);
        setTimeout(() => setShowIntroOverlay(false), 600);
      }
    }, 6000);
    return () => clearTimeout(fallback);
  }, [showIntroOverlay, introFadingOut]);

  return (
    <>
      {/* Loading intro overlay — shown after long idle (>4h), plays video fully then fades out */}
      {showIntroOverlay && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            backgroundColor: '#0a0a0a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: introFadingOut ? 0 : 1,
            transition: 'opacity 0.6s ease-out',
            pointerEvents: introFadingOut ? 'none' : 'auto',
          }}
        >
          <video
            autoPlay
            muted
            playsInline
            onEnded={handleIntroVideoEnded}
            style={{
              width: '100vw',
              height: '100vh',
              objectFit: 'cover',
            }}
          >
            <source src={waitingIntroVideo} type="video/mp4" />
          </video>
        </div>
      )}
      <ApiProvider>
        <AuthProvider>
          <MFAProvider>
            <QuotaProvider>
              <SuiWalletContextProvider>
                <UnifiedWeb3WalletProvider defaultWalletType={WalletType.STELLAR}>
                  <AuthenticatedContent />
                </UnifiedWeb3WalletProvider>
              </SuiWalletContextProvider>
            </QuotaProvider>
          </MFAProvider>
        </AuthProvider>
      </ApiProvider>
    </>
  );
}

// Ce composant utilise useAuth et est correctement encapsulé dans AuthProvider
function AuthenticatedContent() {
  const location = useLocation();
  const navigate = useNavigate();

  const [tabValue, setTabValue] = React.useState(TabIndex.Hub);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const { handleGoogleSignIn, isAuthenticated, globalData, refreshGlobalData, setSelectedWorkspace } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const { mfaState } = useMFA();
  const hasAnyMfaConfigured = (mfaState?.methods?.some((m) => m.isConfigured) ?? false);
  const is2FAEnabled = mfaState?.status?.isEnabled || hasAnyMfaConfigured;
  

  // Unified setup banner state (combines profile + 2FA warnings into one red banner)
  const [isSetupBannerVisible, setIsSetupBannerVisible] = React.useState(false);
  const [isSetupBannerDismissed, setIsSetupBannerDismissed] = React.useState(false);

  // 1-minute delay before showing + 30-minute cooldown after dismiss
  React.useEffect(() => {
    if (!isAuthenticated || !globalData?.isReady) return;
    if (is2FAEnabled) {
      // 2FA is enabled — no banner needed
      setIsSetupBannerVisible(false);
      return;
    }
    // Check 30-minute cooldown
    try {
      const lastDismissed = localStorage.getItem('setupBannerDismissedAt');
      if (lastDismissed) {
        const elapsed = Date.now() - parseInt(lastDismissed, 10);
        if (elapsed < 30 * 60 * 1000) {
          // Still within 30-min cooldown
          setIsSetupBannerDismissed(true);
          // Schedule re-show after remaining cooldown
          const remaining = 30 * 60 * 1000 - elapsed;
          const cooldownTimer = setTimeout(() => {
            setIsSetupBannerDismissed(false);
          }, remaining);
          return () => clearTimeout(cooldownTimer);
        }
      }
    } catch { /* ignore */ }
    // 1-minute delay before showing
    const delayTimer = setTimeout(() => {
      setIsSetupBannerVisible(true);
    }, 60 * 1000);
    return () => clearTimeout(delayTimer);
  }, [isAuthenticated, globalData?.isReady, is2FAEnabled]);

  const handleSetupBannerDismiss = () => {
    setIsSetupBannerDismissed(true);
    try {
      localStorage.setItem('setupBannerDismissedAt', String(Date.now()));
    } catch { /* ignore */ }
  };

  const handleSetupBannerClick = () => {
    if (isProfileSetupIncomplete) {
      // Profile incomplete — open at step 0 (welcome/first step)
      setAccountSettingsInitialStep(0);
    } else {
      // Profile complete — open at step 3 (2FA step)
      setAccountSettingsInitialStep(3);
    }
    setShowAccountSettingsTutorial(true);
  };

  // Whether the unified setup banner should actually render
  const showSetupBanner = isAuthenticated && !is2FAEnabled && isSetupBannerVisible && !isSetupBannerDismissed;
  const showReposDisabledBanner =
    isAuthenticated &&
    globalData?.isReady &&
    globalData?.organization?.reposDisabled === true &&
    !location.pathname.startsWith('/hub');
  const globalBannerCount = (showReposDisabledBanner ? 1 : 0) + (showSetupBanner ? 1 : 0);

  const handleReposDisabledBannerClick = () => {
    navigate('/organization?tab=billing');
  };

  // Global tutorial state
  const [showTutorial, setShowTutorial] = React.useState(false);
  const firstName = globalData?.userProfile?.firstName || 'User';
  
  // New tutorial states for individual accounts
  const [showSecurityPrompt, setShowSecurityPrompt] = React.useState(false);
  const [showAccountSettingsTutorial, setShowAccountSettingsTutorial] = React.useState(false);
  const [accountSettingsInitialStep, setAccountSettingsInitialStep] = React.useState<number | undefined>(undefined);
  const [showGatewayTutorial, setShowGatewayTutorial] = React.useState(false);
  const [showWelcomeTutorial, setShowWelcomeTutorial] = React.useState(false);
  
  // Flashgate guided tutorial state
  const [showCloudGatewayTutorial, setShowCloudGatewayTutorial] = React.useState(false);
  const [cloudGatewayTutorialStep, setCloudGatewayTutorialStep] = React.useState(0);
  const [aiConfigsCount, setAiConfigsCount] = React.useState(0);
  const [reposCount, setReposCount] = React.useState(0);
  const [hasApiKey, setHasApiKey] = React.useState(false);
  const [hasFirstTokenConsumed, setHasFirstTokenConsumed] = React.useState(false);
  
  // Flashgate Tutorial Launcher state (visible site-wide)
  const [showTutorialLauncher, setShowTutorialLauncher] = React.useState(false);
  const [tutorialLauncherStep, setTutorialLauncherStep] = React.useState(0);
  
  // DePIN Hub Tutorial state
  const [showDePINHubTutorial, setShowDePINHubTutorial] = React.useState(false);
  const [depinTutorialStep, setDepinTutorialStep] = React.useState(0);
  const [isWalletConnected, setIsWalletConnected] = React.useState(false);
  const [hasBuckets, setHasBuckets] = React.useState(false);
  const [hasMarketplaceVisited, setHasMarketplaceVisited] = React.useState(false);
  const [hasDeals, setHasDeals] = React.useState(false);
  
  // Global ProfileModal state (for pages where Sidebar is hidden like Chat, Hub)
  const [globalProfileModalOpen, setGlobalProfileModalOpen] = React.useState(false);
  const [globalProfileModalSection, setGlobalProfileModalSection] = React.useState<'Profile' | 'Organization' | 'Plan' | 'Billing' | 'Wallet' | 'Referrals'>('Profile');
  const [globalProfileModalTab, setGlobalProfileModalTab] = React.useState<'Personal' | 'Notifications' | 'Security' | undefined>(undefined);
  const [globalProfileModalHighlight2FA, setGlobalProfileModalHighlight2FA] = React.useState(false);
  
  // Check if user is individual (not part of organization)
  const isIndividualAccount = !globalData?.hasOrgAccess;
  const hasWorkspaces = (globalData?.workspaces?.length || 0) > 0;
  
  // Show welcome tutorial only at onboarding: individual account with no workspaces
  React.useEffect(() => {
    if (!isAuthenticated || !globalData?.isReady) return;
    // Only show for individual accounts with no workspaces (truly new users)
    if (isIndividualAccount && !hasWorkspaces) {
      try {
        const welcomeCompleted = localStorage.getItem('welcomeTutorialCompleted');
        if (!welcomeCompleted) {
          setShowWelcomeTutorial(true);
        }
      } catch { /* ignore */ }
    }
  }, [isAuthenticated, globalData?.isReady, isIndividualAccount, hasWorkspaces]);
  
  // Check if tutorial launcher should be shown (site-wide)
  const isGatewayIntroCompleted = React.useMemo(() => {
    try {
      return localStorage.getItem('cloudGatewayIntroCompleted') === 'true';
    } catch { return false; }
  }, []);
  
  const isTutorialCompleted = React.useMemo(() => {
    try {
      return localStorage.getItem('gatewayTutorialCompleted') === 'true';
    } catch { return false; }
  }, []);
  
  const isTutorialDismissed = React.useMemo(() => {
    try {
      return localStorage.getItem('gatewayTutorialDismissed') === 'true';
    } catch { return false; }
  }, []);
  
  // Show tutorial launcher site-wide if conditions are met
  React.useEffect(() => {
    if (!isAuthenticated || !globalData.isReady) return;
    
    // Show tutorial launcher if:
    // 1. Gateway intro is completed OR user has workspaces
    // 2. Tutorial is not completed
    // 3. Tutorial is not dismissed
    // 4. Flashgate tutorial is not currently active
    if ((isGatewayIntroCompleted || hasWorkspaces) && !isTutorialCompleted && !isTutorialDismissed && !showCloudGatewayTutorial) {
      const timer = setTimeout(() => {
        setShowTutorialLauncher(true);
        // Load saved step
        try {
          const savedStep = localStorage.getItem('gatewayTutorialStep');
          if (savedStep) setTutorialLauncherStep(parseInt(savedStep, 10));
        } catch { /* ignore */ }
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setShowTutorialLauncher(false);
    }
  }, [isAuthenticated, globalData.isReady, hasWorkspaces, isGatewayIntroCompleted, isTutorialCompleted, isTutorialDismissed, showCloudGatewayTutorial]);
  
  // Tutorial launcher handlers
  const handleStartTutorialFromLauncher = () => {
    setShowTutorialLauncher(false);
    // Dispatch event to start tutorial
    window.dispatchEvent(new CustomEvent('startCloudGatewayTutorial'));
  };
  
  const handleDismissTutorialLauncher = () => {
    try {
      localStorage.setItem('gatewayTutorialDismissed', 'true');
    } catch { /* ignore */ }
    setShowTutorialLauncher(false);
  };
  
  const handleWelcomeTutorialComplete = () => {
    setShowWelcomeTutorial(false);
    try {
      localStorage.setItem('welcomeTutorialCompleted', 'true');
    } catch { /* ignore */ }
  };
  
  // Check if profile setup is incomplete (actual profile fields not filled)
  const isProfileSetupIncomplete = React.useMemo(() => {
    if (!isAuthenticated) return false;
    const profile = globalData?.userProfile;
    // Profile is incomplete if firstName or lastName is missing
    return !profile?.firstName || !profile?.lastName;
  }, [isAuthenticated, globalData?.userProfile]);
  
  
  const handleOpenTutorial = () => {
    setShowTutorial(true);
  };

  const handleCloseTutorial = () => {
    setShowTutorial(false);
  };

  const handleCompleteTutorial = () => {
    setShowTutorial(false);
    try {
      localStorage.setItem('platformTutorialCompleted', 'true');
    } catch { void 0; }
  };
  
  // Security prompt handlers
  const handleSetup2FA = () => {
    setShowSecurityPrompt(false);
    // Open profile modal on Security tab
    window.dispatchEvent(new CustomEvent('openProfileModal', { detail: { section: 'Profile', tab: 'Security' } }));
    try {
      localStorage.setItem('securityPromptShown', 'true');
    } catch { void 0; }
  };
  
  const handleSkipSecurity = () => {
    setShowSecurityPrompt(false);
    try {
      localStorage.setItem('securityPromptSkipped', 'true');
      localStorage.setItem('securityPromptShown', 'true');
    } catch { void 0; }
    // Show account settings tutorial after skipping security
    const tutorialCompleted = localStorage.getItem('accountSettingsTutorialCompleted');
    if (!tutorialCompleted && isIndividualAccount) {
      setShowAccountSettingsTutorial(true);
    }
  };
  
  // Account settings tutorial handlers
  const handleAccountSettingsComplete = () => {
    setShowAccountSettingsTutorial(false);
    // Show gateway tutorial if no workspaces
    if (!hasWorkspaces) {
      const gatewayTutorialCompleted = localStorage.getItem('gatewayTutorialCompleted');
      if (!gatewayTutorialCompleted) {
        setShowGatewayTutorial(true);
      }
    }
  };
  
  const handleAccountSettingsNavigate = (section: 'profile' | 'security' | 'notifications' | '2fa') => {
    setShowAccountSettingsTutorial(false);
    if (section === '2fa' || section === 'security') {
      window.dispatchEvent(new CustomEvent('openProfileModal', { detail: { section: 'Profile', tab: 'Security' } }));
    } else if (section === 'profile') {
      window.dispatchEvent(new CustomEvent('openProfileModal', { detail: { section: 'Profile', tab: 'Personal' } }));
    } else if (section === 'notifications') {
      window.dispatchEvent(new CustomEvent('openProfileModal', { detail: { section: 'Profile', tab: 'Notifications' } }));
    }
  };
  
  // Gateway tutorial handlers
  const handleGatewayComplete = () => {
    setShowGatewayTutorial(false);
  };
  
  const handleCreateWorkspace = async (name: string) => {
    // Create workspace via API
    console.log('[GatewayTutorial] Creating workspace:', name);
    try {
      const result = await withTokenRefresh(() => 
        apiClient.createWorkspace({ name: name.trim() })
      );
      
      if (result.success && result.workspace) {
        console.log('[GatewayTutorial] Workspace created:', result.workspace);
        // Refresh global data to include the new workspace
        await refreshGlobalData();
        // Select the new workspace
        setSelectedWorkspace(result.workspace.id);
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('workspaceChanged', { detail: { workspaceId: result.workspace.id } }));
      } else {
        console.error('[GatewayTutorial] Failed to create workspace:', result);
      }
    } catch (error) {
      console.error('[GatewayTutorial] Error creating workspace:', error);
    }
  };
  
  const handleGatewayNavigate = (section: 'cloud-storage' | 'repositories' | 'ai') => {
    setShowGatewayTutorial(false);
    if (section === 'cloud-storage') {
      navigate('/cloud-storage/buckets');
    } else if (section === 'repositories') {
      navigate('/repositories/inventory');
    } else if (section === 'ai') {
      navigate('/ai/llm');
    }
  };
  
  // Check if should show security prompt for individual accounts on first login
  React.useEffect(() => {
    console.log('[Tutorial] Checking tutorial trigger:', {
      isAuthenticated,
      isReady: globalData?.isReady,
      hasOrgAccess: globalData?.hasOrgAccess,
      isIndividualAccount,
    });
    
    if (!isAuthenticated) {
      console.log('[Tutorial] Not authenticated, skipping');
      return;
    }
    
    if (!globalData?.isReady) {
      console.log('[Tutorial] Global data not ready, skipping');
      return;
    }
    
    // Skip if profile is complete AND 2FA is enabled — nothing to set up
    const profileComplete = !!(globalData?.userProfile?.firstName && globalData?.userProfile?.lastName);
    if (profileComplete && is2FAEnabled) {
      console.log('[Tutorial] Profile complete + 2FA enabled, skipping setup prompt');
      return;
    }
    
    try {
      const securityPromptShown = localStorage.getItem('securityPromptShown');
      const tutorialCompleted = localStorage.getItem('accountSettingsTutorialCompleted');
      console.log('[Tutorial] securityPromptShown:', securityPromptShown, 'tutorialCompleted:', tutorialCompleted);
      
      if (!securityPromptShown && !tutorialCompleted) {
        console.log('[Tutorial] Showing security prompt in 1 second...');
        // Small delay to let the UI settle
        setTimeout(() => {
          console.log('[Tutorial] Setting showSecurityPrompt to true');
          setShowSecurityPrompt(true);
        }, 1000);
      } else {
        console.log('[Tutorial] Security prompt already shown before');
      }
    } catch (e) { 
      console.error('[Tutorial] Error:', e);
    }
  }, [isAuthenticated, globalData?.isReady, globalData?.hasOrgAccess, isIndividualAccount, is2FAEnabled, globalData?.userProfile]);

  // Listen for openTutorial event (from Home's Platform Tour button)
  React.useEffect(() => {
    const handleTutorialEvent = () => {
      setShowTutorial(true);
    };
    window.addEventListener('openTutorial', handleTutorialEvent);
    return () => {
      window.removeEventListener('openTutorial', handleTutorialEvent);
    };
  }, []);
  
  // Listen for openAccountSettingsTutorial event
  React.useEffect(() => {
    const handleEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ initialStep?: number }>;
      const step = customEvent.detail?.initialStep;
      if (step !== undefined) {
        setAccountSettingsInitialStep(step);
      }
      setShowAccountSettingsTutorial(true);
    };
    window.addEventListener('openAccountSettingsTutorial', handleEvent);
    return () => {
      window.removeEventListener('openAccountSettingsTutorial', handleEvent);
    };
  }, []);
  
  // Listen for openGatewayTutorial event
  React.useEffect(() => {
    const handleEvent = () => {
      setShowGatewayTutorial(true);
    };
    window.addEventListener('openGatewayTutorial', handleEvent);
    return () => {
      window.removeEventListener('openGatewayTutorial', handleEvent);
    };
  }, []);
  
  // Listen for startCloudGatewayTutorial event (from Home.tsx launcher)
  React.useEffect(() => {
    const handleStartTutorial = () => {
      // Load saved step
      try {
        const savedStep = localStorage.getItem('gatewayTutorialStep');
        if (savedStep) setCloudGatewayTutorialStep(parseInt(savedStep, 10));
      } catch { /* ignore */ }
      setShowCloudGatewayTutorial(true);
    };
    window.addEventListener('startCloudGatewayTutorial', handleStartTutorial);
    return () => {
      window.removeEventListener('startCloudGatewayTutorial', handleStartTutorial);
    };
  }, []);
  
  // Listen for startDePINHubTutorial event
  React.useEffect(() => {
    const handleStartDePINTutorial = () => {
      try {
        const savedStep = localStorage.getItem('depinTutorialStep');
        if (savedStep) setDepinTutorialStep(parseInt(savedStep, 10));
      } catch { /* ignore */ }
      setShowDePINHubTutorial(true);
    };
    window.addEventListener('startDePINHubTutorial', handleStartDePINTutorial);
    return () => {
      window.removeEventListener('startDePINHubTutorial', handleStartDePINTutorial);
    };
  }, []);
  
  // DePIN Tutorial handlers
  const handleDePINTutorialStepChange = (step: number) => {
    setDepinTutorialStep(step);
    try { localStorage.setItem('depinTutorialStep', String(step)); } catch { /* ignore */ }
  };
  
  const handleDePINTutorialComplete = () => {
    setShowDePINHubTutorial(false);
    setDepinTutorialStep(4);
    try {
      localStorage.setItem('depinTutorialCompleted', 'true');
      localStorage.setItem('depinTutorialStep', '4');
    } catch { /* ignore */ }
  };
  
  const handleDePINTutorialClose = () => {
    setShowDePINHubTutorial(false);
  };
  
  // Listen for workspaceCreated event to force refresh workspaces
  React.useEffect(() => {
    const handleWorkspaceCreated = async (event: Event) => {
      const customEvent = event as CustomEvent<{ workspaceId: string }>;
      console.log('[App] Workspace created event received:', customEvent.detail?.workspaceId);
      // Force refresh global data to update workspace list
      await refreshGlobalData(true);
      // Select the new workspace if provided
      if (customEvent.detail?.workspaceId) {
        setSelectedWorkspace(customEvent.detail.workspaceId);
      }
    };
    window.addEventListener('workspaceCreated', handleWorkspaceCreated);
    return () => {
      window.removeEventListener('workspaceCreated', handleWorkspaceCreated);
    };
  }, [refreshGlobalData, setSelectedWorkspace]);
  
  // Listen for openProfileModal event (ONLY for pages where Sidebar is hidden like Chat, Hub)
  // The Sidebar component has its own ProfileModal that handles this event on other pages.
  React.useEffect(() => {
    const handleOpenProfileModal = (event: Event) => {
      // Only open the global modal when the sidebar is NOT rendered (Hub/Chat pages)
      const path = window.location.pathname;
      const sidebarHidden = path.startsWith('/hub') || path.startsWith('/chat');
      if (!sidebarHidden) return; // Sidebar's own ProfileModal will handle it

      const customEvent = event as CustomEvent<{ section?: string; tab?: string; highlight2FA?: boolean }>;
      const { section, tab, highlight2FA } = customEvent.detail || {};
      console.log('[App] openProfileModal event received (sidebar hidden):', { section, tab, highlight2FA });
      setGlobalProfileModalSection((section as any) || 'Profile');
      if (tab) setGlobalProfileModalTab(tab as any);
      if (highlight2FA) setGlobalProfileModalHighlight2FA(true);
      setGlobalProfileModalOpen(true);
    };
    window.addEventListener('openProfileModal', handleOpenProfileModal);
    return () => {
      window.removeEventListener('openProfileModal', handleOpenProfileModal);
    };
  }, []);
  
  // Listen for AI LLM created event (to update tutorial state)
  React.useEffect(() => {
    const handleAiLlmCreated = () => {
      console.log('[App] AI LLM created event received');
      setAiConfigsCount(prev => prev + 1);
    };
    window.addEventListener('aiLlmCreated', handleAiLlmCreated);
    return () => {
      window.removeEventListener('aiLlmCreated', handleAiLlmCreated);
    };
  }, []);
  
  // Listen for repo created event (to update tutorial state)
  React.useEffect(() => {
    const handleRepoCreated = () => {
      console.log('[App] Repo created event received');
      setReposCount(prev => prev + 1);
    };
    window.addEventListener('repoCreated', handleRepoCreated);
    return () => {
      window.removeEventListener('repoCreated', handleRepoCreated);
    };
  }, []);
  
  // Listen for API key created event (to update tutorial state)
  React.useEffect(() => {
    const handleApiKeyCreated = () => {
      console.log('[App] API key created event received');
      setHasApiKey(true);
    };
    window.addEventListener('repoApiKeyCreated', handleApiKeyCreated);
    return () => {
      window.removeEventListener('repoApiKeyCreated', handleApiKeyCreated);
    };
  }, []);
  
  // Load initial tutorial state (AI configs, repos, API keys count)
  // Wait for globalData.isReady to avoid firing API calls with a temporary
  // or not-yet-refreshed token (during MFA or right after page reload).
  React.useEffect(() => {
    const loadTutorialState = async () => {
      if (!isAuthenticated || !apiClient || !globalData?.isReady) return;
      try {
        // Load AI LLMs count (response field is .aiLlms, not .llms)
        const aiResponse = await apiClient.getAiLlms() as any;
        const aiLlms = aiResponse?.aiLlms || aiResponse?.llms || [];
        setAiConfigsCount(Array.isArray(aiLlms) ? aiLlms.length : 0);
        
        // Load repos count
        const reposResponse = await apiClient.getStorageRepos() as any;
        const repos = reposResponse?.repos || [];
        if (Array.isArray(repos)) {
          setReposCount(repos.length);
          
          // Check if any repo has API keys
          const hasKeys = repos.some((repo: any) => 
            (repo.apiKeys?.length > 0) || (repo.aiApiKeys?.length > 0)
          );
          setHasApiKey(hasKeys);
        }
      } catch (e) {
        console.error('[App] Error loading tutorial state:', e);
      }
    };
    
    loadTutorialState();
  }, [isAuthenticated, apiClient, globalData?.isReady]);
  
  // Flashgate tutorial handlers
  const handleCloudGatewayTutorialStepChange = (step: number) => {
    setCloudGatewayTutorialStep(step);
    try { localStorage.setItem('gatewayTutorialStep', String(step)); } catch { /* ignore */ }
  };
  
  const handleCloudGatewayTutorialComplete = () => {
    setShowCloudGatewayTutorial(false);
    setCloudGatewayTutorialStep(4);
    try {
      localStorage.setItem('gatewayTutorialCompleted', 'true');
      localStorage.setItem('gatewayTutorialStep', '4');
    } catch { /* ignore */ }
  };
  
  const handleCloudGatewayTutorialClose = () => {
    setShowCloudGatewayTutorial(false);
  };
  
  // Check if user has consumed any tokens (for tutorial completion) via V2 breakdown
  React.useEffect(() => {
    const checkTokenConsumption = async () => {
      if (!isAuthenticated || !apiClient) return;
      try {
        const response = await apiClient.getAiGatewayBreakdown('models', { scope: 'org', window: '30d' } as any) as any;
        if (response?.entities && Array.isArray(response.entities)) {
          const totalTokens = response.entities.reduce((acc: number, entity: any) => {
            return acc + (entity.totals?.tokensIn || 0) + (entity.totals?.tokensOut || 0);
          }, 0);
          setHasFirstTokenConsumed(totalTokens > 0);
        }
      } catch (e) {
        // Ignore errors - just means we can't check token consumption
      }
    };
    
    if (showCloudGatewayTutorial && cloudGatewayTutorialStep >= 3) {
      // Only check when on the last step
      checkTokenConsumption();
      // Poll every 5 seconds while on last step
      const interval = setInterval(checkTokenConsumption, 5000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, apiClient, showCloudGatewayTutorial, cloudGatewayTutorialStep]);
  
  // Listen for triggerMicroTutorial event (from Sidebar when user has no workspaces)
  React.useEffect(() => {
    const handleMicroTutorial = (event: CustomEvent<{ type: 'gateway' | 'depin' }>) => {
      const { type } = event.detail;
      console.log('[MicroTutorial] Triggered:', type);
      
      if (type === 'gateway') {
        setShowGatewayTutorial(true);
      } else if (type === 'depin') {
        // For ISP Market, we use the same Gateway tutorial but could create a separate one later
        setShowGatewayTutorial(true);
      }
    };
    
    window.addEventListener('triggerMicroTutorial', handleMicroTutorial as EventListener);
    return () => {
      window.removeEventListener('triggerMicroTutorial', handleMicroTutorial as EventListener);
    };
  }, []);

  // Set initial tab based on current URL pathname
  React.useEffect(() => {
    if (!isAuthenticated) return;

    const path = location.pathname;
    console.log('[App] Reading URL on mount/change - pathname:', path);

    // Map pathname to TabIndex
    if (path === '/workspace/home') {
      console.log('[App] Setting tab to Home');
      setTabValue(TabIndex.Home);
    } else if (path === '/' || path === '') {
      // Always redirect to Hub after login
      console.log('[App] Redirecting to Hub after login');
      setTabValue(TabIndex.Hub);
      navigate('/hub', { replace: true });
    } else if (path.startsWith('/cloud-storage/overview')) {
      setTabValue(TabIndex.Dashboard);
    } else if (path.startsWith('/cloud-storage/buckets')) {
      setTabValue(TabIndex.StorageUnits);
    } else if (path.startsWith('/cloud-storage/statistics')) {
      setTabValue(TabIndex.Metrics);
    } else if (path.startsWith('/cloud-storage')) {
      setTabValue(TabIndex.CloudStorage);
    } else if (path.startsWith('/repositories/inventory')) {
      setTabValue(TabIndex.StorageRepos);
    } else if (path.startsWith('/repositories/api-keys')) {
      setTabValue(TabIndex.APIKeys);
    } else if (path.startsWith('/api-keys')) {
      setTabValue(TabIndex.ResourceAPIKeys);
    } else if (path.startsWith('/repositories/overview')) {
      setTabValue(TabIndex.RepoOverview);
    } else if (path.startsWith('/network/public-nodes')) {
      setTabValue(TabIndex.BridgeNodesPublic);
    } else if (path.startsWith('/network/private-nodes')) {
      setTabValue(TabIndex.BridgeNodesPrivate);
    } else if (path.startsWith('/network')) {
      setTabValue(TabIndex.BridgeNodes);
    } else if (path.startsWith('/depin-hub/storage-units')) {
      setTabValue(TabIndex.StorageUnitsDePIN);
    } else if (path.startsWith('/stellar-buckets')) {
      setTabValue(TabIndex.StorageUnitsDePIN);
    } else if (path.startsWith('/sui-buckets')) {
      setTabValue(TabIndex.StorageUnitsDePIN);
    } else if (path.startsWith('/depin-hub/marketplace')) {
      setTabValue(TabIndex.Marketplace);
    } else if (path.startsWith('/depin-hub/deals')) {
      setTabValue(TabIndex.MyDeals);
    } else if (path.startsWith('/performance')) {
      setTabValue(TabIndex.Performance);
    } else if (path.startsWith('/file-explorer')) {
      setTabValue(TabIndex.FileExplorer);
    } else if (path.startsWith('/activity')) {
      setTabValue(TabIndex.Activity);
    } else if (path.startsWith('/feedback')) {
      setTabValue(TabIndex.Feedback);
    } else if (path.startsWith('/ai/overview')) {
      setTabValue(TabIndex.AIOverview);
    } else if (path.startsWith('/ai/llm')) {
      setTabValue(TabIndex.AILLM);
    } else if (path.startsWith('/ai/statistics')) {
      setTabValue(TabIndex.AIStatistics);
    } else if (path.startsWith('/ai/policies')) {
      setTabValue(TabIndex.AIPolicies);
    } else if (path.startsWith('/organization')) {
      setTabValue(TabIndex.Organization);
    } else if (path.startsWith('/activation-links')) {
      setTabValue(TabIndex.ActivationLinks);
    } else if (path.startsWith('/hub')) {
      setTabValue(TabIndex.Hub);
    } else if (path.startsWith('/chat')) {
      setTabValue(TabIndex.Chat);
    } else if (path.startsWith('/depin-hub')) {
      // ISP Market has its own page with custom sidebar
      setTabValue(TabIndex.StorageUnitsDePIN);
    }
  }, [location.pathname, isAuthenticated]);

  // Payment confirmation modal state
  const [paymentModalOpen, setPaymentModalOpen] = React.useState(false);
  const [paymentSuccess, setPaymentSuccess] = React.useState(false);
  const [paymentSessionId, setPaymentSessionId] = React.useState<string | undefined>(undefined);

  // Detect Stripe redirect and show payment confirmation
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const canceled = params.get('canceled');

    if (sessionId) {
      // Payment successful
      setPaymentSuccess(true);
      setPaymentSessionId(sessionId);
      setPaymentModalOpen(true);
      // Clean URL
      const cleanUrl = location.pathname;
      navigate(cleanUrl, { replace: true });
    } else if (canceled === '1') {
      // Payment canceled
      setPaymentSuccess(false);
      setPaymentSessionId(undefined);
      setPaymentModalOpen(true);
      // Clean URL
      const cleanUrl = location.pathname;
      navigate(cleanUrl, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  React.useEffect(() => {
    // Écouteur d'événement pour la navigation vers la section Buckets
    const handleNavigateToBuckets = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.tabIndex !== undefined) {
        setTabValue(customEvent.detail.tabIndex);
      }
    };

    // Écouteur d'événement pour la navigation vers d'autres onglets
    const handleNavigateToTab = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.tabIndex !== undefined) {
        setTabValue(customEvent.detail.tabIndex);
      }
    };

    // Ajouter les écouteurs d'événements
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.addEventListener('navigateToBuckets', handleNavigateToBuckets);
      rootElement.addEventListener('navigateToTab', handleNavigateToTab);
    }

    return () => {
      // Supprimer les écouteurs d'événements lors du nettoyage
      if (rootElement) {
        rootElement.removeEventListener('navigateToBuckets', handleNavigateToBuckets);
        rootElement.removeEventListener('navigateToTab', handleNavigateToTab);
      }
    };
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Navigate directly to the correct URL for special pages
    const directNavigationUrls: Record<number, string> = {
      [TabIndex.Hub]: '/hub',
      [TabIndex.Chat]: '/chat',
      [TabIndex.Organization]: '/organization',
      [TabIndex.Home]: '/workspace/home',
      [TabIndex.StorageUnitsDePIN]: '/depin-hub/storage-units',
    };
    
    const targetUrl = directNavigationUrls[newValue];
    if (targetUrl && location.pathname !== targetUrl) {
      navigate(targetUrl);
    }
  };

  // Normalize URL to a canonical base per tab when switching tabs
  // Use a ref to prevent redirect loops
  const isRedirectingRef = React.useRef(false);
  const lastNormalizedPathRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    // Do not normalize routes when not authenticated (prevents overriding /auth, etc.)
    if (!isAuthenticated) return;

    // Prevent redirect loops - if we're already redirecting or the path is already normalized, skip
    if (isRedirectingRef.current) return;
    if (lastNormalizedPathRef.current === location.pathname) return;

    // Do not redirect away from magic link page - it must process token then redirect itself
    if (location.pathname === '/mfa/magic-link/activate') return;

    console.log('[App] URL Normalization Effect - tabValue:', tabValue, 'pathname:', location.pathname);

    const baseByTab: Record<number, string> = {
      [TabIndex.Home]: '/workspace/home',
      [TabIndex.Dashboard]: '/cloud-storage/overview',
      [TabIndex.CloudStorage]: '/cloud-storage',
      [TabIndex.StorageUnits]: '/cloud-storage/buckets',
      [TabIndex.StorageRepos]: '/repositories/inventory',
      [TabIndex.BridgeNodes]: '/network',
      [TabIndex.Performance]: '/performance',
      [TabIndex.Metrics]: '/cloud-storage/statistics',
      [TabIndex.FileExplorer]: '/file-explorer',
      [TabIndex.Activity]: '/activity',
      [TabIndex.Feedback]: '/feedback',
      [TabIndex.BridgeNodesPublic]: '/network/public-nodes',
      [TabIndex.BridgeNodesPrivate]: '/network/private-nodes',
      [TabIndex.AIOverview]: '/ai/overview',
      [TabIndex.AILLM]: '/ai/llm',
      [TabIndex.AIStatistics]: '/ai/statistics',
      [TabIndex.AIPolicies]: '/ai/policies',
      [TabIndex.APIKeys]: '/repositories/api-keys',
      [TabIndex.ResourceAPIKeys]: '/api-keys',
      [TabIndex.Organization]: '/organization',
      [TabIndex.ActivationLinks]: '/activation-links',
      [TabIndex.RepoOverview]: '/repositories/overview',
      [TabIndex.Hub]: '/hub',
      [TabIndex.Chat]: '/chat',
      [TabIndex.StorageUnitsDePIN]: '/depin-hub/storage-units',
      [TabIndex.Marketplace]: '/depin-hub/marketplace',
      [TabIndex.MyDeals]: '/depin-hub/deals',
    } as const;

    const base = baseByTab[tabValue];
    if (!base) return;

    // Special handling for Home - must be exact match
    if (tabValue === TabIndex.Home) {
      if (location.pathname !== '/workspace/home') {
        console.log('[App] Redirecting to Home');
        isRedirectingRef.current = true;
        lastNormalizedPathRef.current = '/workspace/home';
        navigate('/workspace/home', { replace: true });
        // Reset redirecting flag after a short delay
        setTimeout(() => {
          isRedirectingRef.current = false;
        }, 100);
      }
      return;
    }

    // Special handling for Hub - must be exact match
    if (tabValue === TabIndex.Hub) {
      if (location.pathname !== '/hub') {
        console.log('[App] Redirecting to Hub');
        isRedirectingRef.current = true;
        lastNormalizedPathRef.current = '/hub';
        navigate('/hub', { replace: true });
        setTimeout(() => {
          isRedirectingRef.current = false;
        }, 100);
      }
      return;
    }

    // Special handling for Chat - must be exact match
    // BUT allow navigation away if user clicked a button to leave (check if path is a known route)
    if (tabValue === TabIndex.Chat) {
      if (location.pathname !== '/chat') {
        // Check if navigating to a known valid route - if so, update tab instead of redirecting back
        const knownRoutes = ['/cloud-storage', '/workspace/home', '/hub', '/repositories', '/ai', '/network', '/metrics', '/organization', '/depin-hub'];
        const isKnownRoute = knownRoutes.some(route => location.pathname.startsWith(route));
        if (isKnownRoute) {
          // User intentionally navigated away - let the URL-based tab update handle it
          console.log('[App] User navigated away from Chat to:', location.pathname);
          return;
        }
        console.log('[App] Redirecting to Chat');
        isRedirectingRef.current = true;
        lastNormalizedPathRef.current = '/chat';
        navigate('/chat', { replace: true });
        setTimeout(() => {
          isRedirectingRef.current = false;
        }, 100);
      }
      return;
    }

    // Always normalize to '/cloud-storage/overview' for Dashboard
    if (tabValue === TabIndex.Dashboard) {
      if (location.pathname !== '/cloud-storage/overview') {
        console.log('[App] Redirecting to /cloud-storage/overview');
        isRedirectingRef.current = true;
        lastNormalizedPathRef.current = '/cloud-storage/overview';
        navigate('/cloud-storage/overview', { replace: true });
        setTimeout(() => {
          isRedirectingRef.current = false;
        }, 100);
      }
      return;
    }

    // Special handling for StorageUnitsDePIN - allow stellar-buckets and sui-buckets paths
    if (tabValue === TabIndex.StorageUnitsDePIN) {
      if (location.pathname.startsWith('/stellar-buckets/') || location.pathname.startsWith('/sui-buckets/')) {
        lastNormalizedPathRef.current = location.pathname;
        return; // Don't redirect, keep the detail page path
      }
    }

    // Check if the current pathname is already a valid route for any tab
    // This prevents redirect loops when tabValue hasn't updated yet after navigation
    const allValidBases = Object.values(baseByTab);
    const isAlreadyValidRoute = allValidBases.some(validBase => location.pathname.startsWith(validBase));
    
    if (!location.pathname.startsWith(base)) {
      // Only redirect if the current path is NOT a recognized route
      // This handles the race condition where tabValue hasn't updated yet
      if (!isAlreadyValidRoute) {
        console.log('[App] Redirecting to', base);
        isRedirectingRef.current = true;
        lastNormalizedPathRef.current = base;
        navigate(base, { replace: true });
        setTimeout(() => {
          isRedirectingRef.current = false;
        }, 100);
      } else {
        // Path is a valid route for another tab, let the tab-setting effect catch up
        console.log('[App] Skipping redirect - path is valid for another tab:', location.pathname);
      }
    } else {
      // Path is already normalized, update the ref
      lastNormalizedPathRef.current = location.pathname;
    }
  }, [tabValue, navigate, location.pathname, isAuthenticated]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <SuiWalletContextProvider>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />

        <Routes>

          <Route path="/auth/github/callback" element={<AuthCallback />} />
          <Route path="/auth/google/callback" element={<AuthCallback />} />
          <Route path="/auth/microsoft/callback" element={<AuthCallback />} />
          <Route path="/overview-mockup" element={<OverviewMockup />} />
          <Route path="/landing-page-v2" element={<LandingPageV2 />} />
          {/* Secure auth route with long random string */}
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/signup" element={<Navigate to="/auth?tab=signup" replace />} />
          <Route path="/register" element={<Navigate to="/auth?tab=signup" replace />} />
          <Route path="/flashback" element={<Navigate to="/" replace />} />
          <Route path="/flashback-platform" element={<Navigate to="/" replace />} />
          <Route path="/platform" element={<Navigate to="/" replace />} />
          <Route path="/cloud-ai-gateway" element={<Navigate to="/" replace />} />
          {/* Public legacy login page, with signup links restored */}
          <Route path="/login" element={<LoginPage />} />
          {/* Additional legacy routes are redirected above */}

          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/mfa-verification"
            element={
              <PostLoginMFAVerification onVerificationSuccess={() => (window.location.href = '/')} />
            }
          />

          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/mfa/magic-link/activate" element={<MagicLinkActivatePage />} />
          <Route
            path="/contact"
            element={
              <div className="flex flex-col h-screen" style={{ backgroundColor: '#FFFFFF' }}>
                <Contact />
              </div>
            }
          />

          <Route
            path="/*"
            element={
              <div className="flex flex-col h-screen" style={{ backgroundColor: '#FFFFFF' }}>
                {/* Navbar hidden - LandingPageV2 has its own header */}

                {/* Afficher la landing page si l'utilisateur n'est pas connecté */}
                {!isAuthenticated ? (
                  <LandingPageV2 />
                ) : (
                  <PostLoginMFAWrapper>
                    <div className="flex flex-1">
                      {/* Mobile burger button - fixed top-left */}
                      {/* Hide Sidebar on Hub and Chat pages */}
                      {!location.pathname.startsWith('/hub') &&
                        !location.pathname.startsWith('/chat') && (
                          <>
                            <button
                              className="lg:hidden fixed top-3 left-4 z-50 w-10 h-10 flex items-center justify-center bg-white rounded-lg shadow-md border border-gray-200 hover:bg-gray-50 transition-colors"
                              onClick={() => setIsMobileMenuOpen(true)}
                              aria-label="Open menu"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 text-gray-700"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 6h16M4 12h16M4 18h16"
                                />
                              </svg>
                            </button>

                            <Sidebar
                              tabValue={tabValue}
                              handleTabChange={handleTabChange}
                              isMobileMenuOpen={isMobileMenuOpen}
                              setIsMobileMenuOpen={setIsMobileMenuOpen}
                              sidebarCollapsed={sidebarCollapsed}
                              onSidebarCollapse={setSidebarCollapsed}
                            />

                            {isMobileMenuOpen && (
                              <div
                                className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
                                onClick={() => setIsMobileMenuOpen(false)}
                              />
                            )}

                            {/* Sidebar toggle bubble - outside sidebar so it stays visible when collapsed */}
                            <button
                              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                              className="hidden lg:flex items-center justify-center w-7 h-7 rounded-full transition-all duration-300 ease-in-out hover:bg-[#EEF2FF] shadow-sm"
                              style={{
                                position: 'fixed',
                                left: sidebarCollapsed ? '16px' : '282px',
                                top: `${78 + globalBannerCount * 36}px`,
                                backgroundColor: 'white',
                                border: '1px solid #617BFF',
                                zIndex: 40,
                                cursor: 'pointer',
                                transition: 'left 0.3s ease, top 0.3s ease'
                              }}
                              title={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#617BFF"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                  transform: sidebarCollapsed ? 'rotate(180deg)' : 'none',
                                  transition: 'transform 0.3s ease'
                                }}
                              >
                                <path d="M15 18l-6-6 6-6" />
                              </svg>
                            </button>
                          </>
                        )}

                      {/* Header - Hidden on Hub page only */}
                      {!location.pathname.startsWith('/hub') && (
                        <Header
                          sidebarCollapsed={sidebarCollapsed}
                          handleTabChange={handleTabChange}
                          tabValue={tabValue}
                        />
                      )}

                      <div
                        className={`flex-1 relative transition-all duration-300 ${
                          location.pathname.startsWith('/hub') ||
                          location.pathname.startsWith('/chat')
                            ? ''
                            : sidebarCollapsed
                              ? 'lg:ml-0 lg:pt-12'
                              : 'lg:ml-[296px]'
                        }`}
                      >
                        {/* Fond modifié pour correspondre au nouveau style */}
                        <div
                          className="absolute inset-0"
                          style={{ backgroundColor: '#FFFFFF' }}
                        ></div>

                        <div
                          className="relative h-full text-gray-700"
                          style={{ 
                            backgroundColor: '#FFFFFF',
                            // Add padding for header (64px) + fixed banners (36px each)
                            paddingTop: location.pathname.startsWith('/hub') ? '0px' : location.pathname.startsWith('/chat') ? '64px' : (() => {
                              const headerHeight = 64;
                              return `${headerHeight + globalBannerCount * 36}px`;
                            })()
                          }}
                        >
                          {showReposDisabledBanner && (
                            <Security2FAWarning
                              onSetup2FA={handleReposDisabledBannerClick}
                              sidebarCollapsed={sidebarCollapsed}
                              message="Repository and node access is currently disabled for your organization (subscription/quota). Check Billing now."
                            />
                          )}
                          {/* Unified Setup Banner - shown when 2FA is not enabled (after 1min delay, with 30min cooldown) */}
                          {showSetupBanner && !location.pathname.startsWith('/hub') && (
                            <Security2FAWarning
                              onSetup2FA={handleSetupBannerClick}
                              onDismiss={handleSetupBannerDismiss}
                              sidebarCollapsed={sidebarCollapsed}
                              message="Profile and 2FA incomplete. Setup now."
                            />
                          )}
                          {location.pathname.startsWith('/profile') ? (
                            <div className="w-full">
                              <Profile />
                            </div>
                          ) : location.pathname.startsWith('/hub') ? (
                            <div className="w-full">
                              <Hub handleTabChange={handleTabChange} />
                            </div>
                          ) : location.pathname.startsWith('/chat') ? (
                            <div className="w-full">
                              <Chat />
                            </div>
                          ) : location.pathname.startsWith('/depin-hub') ? (
                            <div className="w-full">
                              <DePINHub handleTabChange={handleTabChange} />
                            </div>
                          ) : (
                            <>
                              <TabPanel value={tabValue} index={TabIndex.Home}>
                                <div className="w-full">
                                  <Home handleTabChange={handleTabChange} />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.Dashboard}>
                                <div className="w-full">
                                  <OverviewMockup />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.CloudStorage}>
                                <div className="w-full">
                                  <CloudStorage handleTabChange={handleTabChange} />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.StorageUnits}>
                                <div className="w-full">
                                  <Routes>
                                    <Route
                                      path="/cloud-storage/buckets/:unitId"
                                      element={<StorageUnitDetails />}
                                    />
                                    <Route
                                      path="/cloud-storage/buckets/add"
                                      element={<AddBucket />}
                                    />
                                    <Route path="*" element={<StorageUnits />} />
                                  </Routes>
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.StorageRepos}>
                                <div className="w-full">
                                  <Routes>
                                    <Route
                                      path="/repositories/inventory/:repoId"
                                      element={<StorageRepoDetails />}
                                    />
                                    <Route
                                      path="/repositories/inventory/add"
                                      element={<AddRepo />}
                                    />
                                    <Route
                                      path="/repositories/inventory/edit/:repoId"
                                      element={<AddRepo />}
                                    />
                                    <Route path="*" element={<StorageRepos />} />
                                  </Routes>
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.BridgeNodes}>
                                <div className="w-full">
                                  <BridgeNodes />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.BridgeNodesPublic}>
                                <div className="w-full">
                                  <BridgeNodes />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.BridgeNodesPrivate}>
                                <div className="w-full">
                                  <PrivateBridgeNodes />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.Performance}>
                                <div className="w-full">
                                  <QoSPerformanceDashboard />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.Metrics}>
                                <div className="w-full">
                                  <MetricsDashboard />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.Marketplace}>
                                <div className="w-full">
                                  <DePINHubMarketplace />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.MyDeals}>
                                <div className="w-full">
                                  <DePINHubDeals />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.StorageUnitsDePIN}>
                                <div className="w-full">
                                  {location.pathname.startsWith('/stellar-buckets/') || location.pathname.startsWith('/sui-buckets/') ? (
                                    <DePINHubBucketDetails />
                                  ) : (
                                    <DePINHubBuckets />
                                  )}
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.AIOverview}>
                                <div className="w-full">
                                  <AIOverview handleTabChange={handleTabChange} />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.AILLM}>
                                <div className="w-full">
                                  {/* Render details page if path is /ai/llm/:id, otherwise render list */}
                                  {location.pathname.match(/^\/ai\/llm\/[^/]+$/) ? (
                                    <AILLMDetails />
                                  ) : (
                                    <AILLM />
                                  )}
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.AIStatistics}>
                                <div className="w-full">
                                  <AIStatistics />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.AIPolicies}>
                                <div className="w-full">
                                  <AIPolicies />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.APIKeys}>
                                <div className="w-full">
                                  <APIKeys />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.ResourceAPIKeys}>
                                <div className="w-full">
                                  <ResourceAPIKeys />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.RepoOverview}>
                                <div className="w-full">
                                  <RepoOverview />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.Activity}>
                                <div className="w-full">
                                  <Activity />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.Feedback}>
                                <div className="w-full">
                                  <Feedback />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.Organization}>
                                <div className="w-full">
                                  {/* Remount Organization when search (e.g., ?tab=workspace) changes */}
                                  <Organization key={location.search || 'org'} />
                                </div>
                              </TabPanel>
                              <TabPanel value={tabValue} index={TabIndex.ActivationLinks}>
                                <div className="w-full">
                                  <ActivationLinks />
                                </div>
                              </TabPanel>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </PostLoginMFAWrapper>
                )}
              </div>
            }
          />
        </Routes>

        {/* Global Chat Button - appears on all authenticated pages */}
        {isAuthenticated && <ChatButton />}

        {/* Global Tutorial Return Bubble - appears when tutorial is in progress */}
        {isAuthenticated && (
          <TutorialReturnBubble onReturnToTutorial={handleOpenTutorial} />
        )}

        {/* Global Onboarding Tutorial - accessible from any page */}
        {isAuthenticated && (
          <OnboardingTutorial
            open={showTutorial}
            onClose={handleCloseTutorial}
            onComplete={handleCompleteTutorial}
            userName={firstName}
          />
        )}
        
        
        {/* Account Settings Tutorial - for all accounts */}
        {isAuthenticated && (
          <AccountSettingsTutorial
            open={showAccountSettingsTutorial}
            onClose={() => { setShowAccountSettingsTutorial(false); setAccountSettingsInitialStep(undefined); }}
            onComplete={handleAccountSettingsComplete}
            onNavigate={handleAccountSettingsNavigate}
            initialStep={accountSettingsInitialStep}
          />
        )}
        
        {/* Gateway Tutorial - for all accounts */}
        {isAuthenticated && (
          <GatewayTutorial
            open={showGatewayTutorial}
            onClose={() => setShowGatewayTutorial(false)}
            onComplete={handleGatewayComplete}
            onCreateWorkspace={handleCreateWorkspace}
            onNavigate={handleGatewayNavigate}
          />
        )}
        
        {/* Welcome Tutorial - for new users (no workspaces) */}
        {isAuthenticated && (
          <WelcomeTutorial
            open={showWelcomeTutorial}
            onComplete={handleWelcomeTutorialComplete}
            userName={firstName}
          />
        )}
        
        {/* Flashgate guided tutorial - with UI highlights */}
        {isAuthenticated && (
          <CloudGatewayTutorial
            isActive={showCloudGatewayTutorial}
            onClose={handleCloudGatewayTutorialClose}
            onComplete={handleCloudGatewayTutorialComplete}
            currentStep={cloudGatewayTutorialStep}
            onStepChange={handleCloudGatewayTutorialStepChange}
            aiConfigsCount={aiConfigsCount}
            reposCount={reposCount}
            hasApiKey={hasApiKey}
            hasFirstTokenConsumed={hasFirstTokenConsumed}
          />
        )}
        
        {/* DePIN Hub Guided Tutorial - with UI highlights */}
        {isAuthenticated && (
          <DePINHubTutorial
            isActive={showDePINHubTutorial}
            onClose={handleDePINTutorialClose}
            onComplete={handleDePINTutorialComplete}
            currentStep={depinTutorialStep}
            onStepChange={handleDePINTutorialStepChange}
            isWalletConnected={isWalletConnected}
            hasBuckets={hasBuckets}
            hasMarketplaceVisited={hasMarketplaceVisited}
            hasDeals={hasDeals}
          />
        )}
        
        {/* Flashgate tutorial launcher - visible only inside Flashgate service */}

        {isAuthenticated && (
          <CloudGatewayTutorialLauncher
            onStartTutorial={handleStartTutorialFromLauncher}
            onDismiss={handleDismissTutorialLauncher}
            currentStep={tutorialLauncherStep}
            totalSteps={4}
            isVisible={showTutorialLauncher && !showCloudGatewayTutorial && (
              location.pathname.startsWith('/cloud-storage') ||
              location.pathname.startsWith('/repositories') ||
              location.pathname.startsWith('/ai') ||
              location.pathname.startsWith('/network') ||
              location.pathname.startsWith('/workspace/home') ||
              location.pathname.startsWith('/performance') ||
              location.pathname.startsWith('/file-explorer') ||
              location.pathname.startsWith('/activity')
            )}
            stepsStatus={[
              { label: 'Add an AI provider', completed: aiConfigsCount > 0 },
              { label: 'Create a repository', completed: reposCount > 0 },
              { label: 'Generate an API key', completed: hasApiKey },
              { label: 'Make your first request', completed: hasFirstTokenConsumed },
            ]}
          />
        )}
        
        {/* Security2FAReminderBanner removed — unified into the single red setup banner at top */}
        
        {/* Global ProfileModal - for pages where Sidebar is hidden (Chat, Hub) */}
        {isAuthenticated && (
          <ProfileModal
            open={globalProfileModalOpen}
            onClose={() => {
              setGlobalProfileModalOpen(false);
              setGlobalProfileModalSection('Profile');
              setGlobalProfileModalTab(undefined);
              setGlobalProfileModalHighlight2FA(false);
            }}
            initialSection={globalProfileModalSection}
            initialTab={globalProfileModalTab}
            highlight2FA={globalProfileModalHighlight2FA}
          />
        )}
        
      </SuiWalletContextProvider>
    </>
  );
}

function App() {
  return <AppLayout />;
}

export default App;
